<template>
  <div class="register-page">
    <div>
      <!-- Header -->
      <div class="viva-page-header">
        <div class="viva-header-content">
          <div class="register-title">
            <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
            <h1 class="viva-page-title">INSTRUCTOR REGISTRATION</h1>
          </div>
          <p class="register-subtitle text-center">
            Create your instructor or administrator account:
          </p>
        </div>
      </div>

      <div class="page-content">
        <form @submit.prevent="handleRegister" class="container-fluid">
          <div class="form-sections row">
            <div class="form-left col-xs-12 col-sm-6 col-md-4">
              <div class="form-group">
                <input
                  type="text"
                  v-model="registerForm.firstName"
                  placeholder="Given / First Name"
                  @blur="validateField('firstName')"
                  class="form-input required"
                  :class="{
                    'invalid-field':
                      validationErrors.firstName && fieldTouched.firstName,
                  }"
                />
                <span class="required-asterisk">*</span>
              </div>

              <div class="form-group">
                <input
                  type="text"
                  v-model="registerForm.lastName"
                  placeholder="Family / Last Name"
                  @blur="validateField('lastName')"
                  class="form-input required"
                  :class="{
                    'invalid-field':
                      validationErrors.lastName && fieldTouched.lastName,
                  }"
                />
                <span class="required-asterisk">*</span>
              </div>

              <div class="form-group">
                <input
                  type="email"
                  v-model="registerForm.email"
                  placeholder="Email"
                  @blur="validateField('email')"
                  class="form-input required"
                  :class="{
                    'invalid-field':
                      validationErrors.email && fieldTouched.email,
                  }"
                />
                <span class="required-asterisk">*</span>
              </div>

              <div class="form-group">
                <input
                  type="email"
                  v-model="registerForm.confirmEmail"
                  placeholder="Confirm Email"
                  @blur="validateField('confirmEmail')"
                  class="form-input required"
                  :class="{
                    'invalid-field':
                      validationErrors.confirmEmail &&
                      fieldTouched.confirmEmail,
                  }"
                />
                <span class="required-asterisk">*</span>
              </div>

              <div class="form-group">
                <input
                  type="email"
                  v-model="registerForm.emailPersonal"
                  placeholder="Additional Email Address (optional)"
                  class="form-input"
                />
              </div>

              <div class="form-group">
                <input
                  type="password"
                  v-model="registerForm.password"
                  placeholder="Password"
                  @blur="validateField('password')"
                  class="form-input required"
                  :class="{
                    'invalid-field':
                      validationErrors.password && fieldTouched.password,
                  }"
                />
                <span class="required-asterisk">*</span>
                <div
                  v-if="validationErrors.password && fieldTouched.password"
                  class="error-message"
                >
                  {{ validationErrors.password }}
                </div>
              </div>

              <div class="form-group">
                <input
                  type="password"
                  v-model="registerForm.confirmPassword"
                  placeholder="Confirm Password"
                  @blur="validateField('confirmPassword')"
                  class="form-input required"
                  :class="{
                    'invalid-field':
                      validationErrors.confirmPassword &&
                      fieldTouched.confirmPassword,
                  }"
                />
                <span class="required-asterisk">*</span>
                <div
                  v-if="
                    validationErrors.confirmPassword &&
                    fieldTouched.confirmPassword
                  "
                  class="error-message"
                >
                  {{ validationErrors.confirmPassword }}
                </div>
              </div>
            </div>

            <div class="form-right col-xs-12 col-sm-6 col-md-8">
              <!-- Role Selection -->
              <div class="role-section">
                <h3 class="section-title">
                  I AM A: <span class="required-asterisk">*</span>
                </h3>
                <div class="role-options">
                  <label
                    class="role-option"
                    :class="{
                      'role-selected': registerForm.roleType === 'instructor',
                    }"
                  >
                    <input
                      type="radio"
                      v-model="registerForm.roleType"
                      value="instructor"
                      class="role-radio"
                    />
                    <span class="role-label">Instructor</span>
                  </label>
                  <label
                    class="role-option"
                    :class="{
                      'role-selected': registerForm.roleType === 'admin',
                    }"
                  >
                    <input
                      type="radio"
                      v-model="registerForm.roleType"
                      value="admin"
                      class="role-radio"
                    />
                    <span class="role-label">School Administrator</span>
                  </label>
                </div>
              </div>

              <!-- School Search -->
              <div class="school-section">
                <h3 class="section-title">
                  SCHOOL / INSTITUTION:
                  <span class="required-asterisk" style="position:relative;top:auto;transform:none;font-size:20px;">*</span>
                </h3>
                <p class="school-instruction">Type school name or CEEB code.</p>

                <div class="search-container">
                  <div class="search-input-wrapper">
                    <i class="fa fa-search search-icon"></i>
                    <input
                      type="text"
                      v-model="schoolSearchQuery"
                      @input="handleSchoolSearch"
                      placeholder="Search schools..."
                      class="school-search-input"
                      :disabled="loading || selectedSchool"
                    />
                    <i
                      v-if="schoolSearchLoading"
                      class="fa fa-spinner fa-spin viva-spinner-small search-loading-icon"
                    ></i>
                  </div>
                </div>

                <!-- Search Results Dropdown -->
                <div
                  v-if="schoolResults.length > 0"
                  class="school-results-dropdown"
                >
                  <div
                    v-for="(school, index) in schoolResults"
                    :key="school.ceeb_code || index"
                    class="school-result-item"
                    @click="selectSchool(school)"
                  >
                    <div class="school-result-content">
                      <div class="school-name">{{ school.name }}</div>
                      <div class="school-location">{{ school.location }}</div>
                      <div v-if="school.ceeb_code" class="school-ceeb">
                        CEEB: {{ school.ceeb_code }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Selected School Display -->
                <div
                  v-if="selectedSchool"
                  class="school-result-content selected-school-display"
                >
                  <div>
                    <div class="school-name">{{ selectedSchool.name }}</div>
                    <div class="school-location">
                      {{ selectedSchool.location }}
                    </div>
                    <div v-if="selectedSchool.ceeb_code" class="school-ceeb">
                      CEEB: {{ selectedSchool.ceeb_code }}
                    </div>
                  </div>
                  <button
                    type="button"
                    @click="clearSchoolSelection"
                    class="viva-btn viva-btn-orange clear-school-btn"
                  >
                    Change School
                  </button>
                </div>

                <!-- No Results Message -->
                <div
                  v-if="
                    schoolSearchQuery &&
                    !schoolSearchLoading &&
                    schoolResults.length === 0 &&
                    searchPerformed
                  "
                  class="no-results-message"
                >
                  No schools found for "{{ schoolSearchQuery }}". Try different
                  keywords or the 6-digit CEEB code.
                </div>

                <!-- Free-text school name fallback -->
                <div v-if="!selectedSchool && schoolResults.length === 0" class="school-name-fallback">
                  <input
                    type="text"
                    v-model="schoolNameFallback"
                    placeholder="Can't find your school? Enter school name here."
                    class="school-search-input"
                    :disabled="loading"
                    maxlength="255"
                  />
                </div>
              </div>

              <div class="terms-section">
                <label class="terms-checkbox">
                  <input
                    type="checkbox"
                    v-model="agreeTerms"
                    class="checkbox-input"
                  />
                  <span class="checkmark"></span>
                  <p class="text-link">
                    I agree to the
                    <a
                      class="link-accent"
                      :href="env.serverAddr + '/viva/terms-and-conditions/'"
                      target="_blank"
                      >terms and conditions</a
                    >,
                    <a
                      class="link-accent"
                      :href="env.serverAddr + '/viva/privacy-policy/'"
                      target="_blank"
                      >privacy policy</a
                    >, and
                    <a
                      class="link-accent"
                      :href="env.serverAddr + '/viva/faqs/'"
                      target="_blank"
                      >FAQs</a
                    >.
                  </p>
                </label>
              </div>

              <div
                v-if="errors.general"
                class="viva-alert viva-alert-danger"
                style="margin-bottom: 20px"
              >
                {{ errors.general }}
              </div>

              <div class="form-actions">
                <button
                  type="submit"
                  class="create-account-btn"
                  :disabled="!isFormValid || loading"
                >
                  <span v-if="loading">
                    <i
                      class="fa fa-spinner fa-spin viva-spinner-small viva-spinner--white"
                      aria-hidden="true"
                    ></i>
                    Submitting...
                  </span>
                  <span v-else>
                    {{
                      registerForm.roleType === "admin"
                        ? "Request an administrator account"
                        : "Request an instructor account"
                    }}
                    <i class="fa fa-arrow-right"></i>
                  </span>
                </button>
              </div>

              <div class="bottom-links">
                <p class="text-link">
                  Not a teacher or administrator?
                  <a
                    class="link-accent"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://initialview.typeform.com/to/wJ1JVuVs"
                    >Click here</a
                  >
                  to join the waitlist.
                </p>
                <p class="text-link">
                  Looking to sign up as a student?
                  <router-link class="link-accent" to="/register"
                    >Click here</router-link
                  >
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div class="back-section">
        <p>
          <router-link class="back-link" to="/">
            <i class="fa fa-arrow-left"></i> Back
          </router-link>
        </p>
      </div>

      <Footer />
    </div>

    <!-- Success Modal -->
    <div v-if="showSuccessModal" class="viva-modal-backdrop">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h3>Thank You!</h3>
        </div>
        <div class="viva-modal-body">
          <p>
            We will review your request. You will receive an email once your
            account is approved.
          </p>
        </div>
        <div class="viva-modal-footer">
          <router-link class="viva-btn viva-btn-green" to="/">
            BACK TO HOME
          </router-link>
        </div>
      </div>
    </div>

    <!-- Account Exists Modal -->
    <div v-if="showAccountExistsModal" class="viva-modal-backdrop">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h3>Account Already Exists</h3>
        </div>
        <div class="viva-modal-body">
          <p>
            It looks like you already have an account. Please try logging in
            instead.
          </p>
        </div>
        <div class="viva-modal-footer">
          <router-link class="viva-btn viva-btn-green" to="/login">
            LOGIN
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "../utils/apiClient.js";
import Footer from "@/components/Footer.vue";
import env from "@/utils/env.js";

export default {
  name: "InstructorRegister",
  components: {
    Footer,
  },
  data() {
    return {
      env: env,
      loading: false,
      errors: {},
      registerForm: {
        firstName: "",
        lastName: "",
        email: "",
        confirmEmail: "",
        password: "",
        confirmPassword: "",
        emailPersonal: "",
        roleType: "instructor",
      },
      validationErrors: {
        firstName: "",
        lastName: "",
        email: "",
        confirmEmail: "",
        password: "",
        confirmPassword: "",
      },
      fieldTouched: {
        firstName: false,
        lastName: false,
        email: false,
        confirmEmail: false,
        password: false,
        confirmPassword: false,
      },
      schoolSearchQuery: "",
      schoolSearchLoading: false,
      schoolResults: [],
      selectedSchool: null,
      searchPerformed: false,
      searchTimeout: null,
      schoolNameFallback: "",
      showSuccessModal: false,
      showAccountExistsModal: false,
      agreeTerms: false,
    };
  },
  computed: {
    isFormValid() {
      return (
        this.registerForm.firstName.trim() &&
        this.registerForm.lastName.trim() &&
        this.isValidEmail(this.registerForm.email) &&
        this.registerForm.email === this.registerForm.confirmEmail &&
        this.registerForm.password.length >= 15 &&
        this.registerForm.password === this.registerForm.confirmPassword &&
        this.registerForm.roleType &&
        (this.registerForm.roleType !== 'admin' || (this.selectedSchool || this.schoolNameFallback.trim())) &&
        this.agreeTerms
      );
    },
  },
  methods: {
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    validateField(field) {
      this.fieldTouched[field] = true;
      switch (field) {
        case "firstName":
          this.validationErrors.firstName = this.registerForm.firstName
            ? ""
            : "First name required";
          break;
        case "lastName":
          this.validationErrors.lastName = this.registerForm.lastName
            ? ""
            : "Last name required";
          break;
        case "email":
          this.validationErrors.email = this.isValidEmail(
            this.registerForm.email,
          )
            ? ""
            : "Invalid email";
          break;
        case "confirmEmail":
          this.validationErrors.confirmEmail =
            this.registerForm.email === this.registerForm.confirmEmail
              ? ""
              : "Emails do not match";
          break;
        case "password":
          if (!this.registerForm.password) {
            this.validationErrors.password = "Password required";
          } else if (this.registerForm.password.length < 15) {
            this.validationErrors.password =
              "Password must be 15 characters or more";
          } else {
            this.validationErrors.password = "";
          }
          break;
        case "confirmPassword":
          this.validationErrors.confirmPassword =
            this.registerForm.password === this.registerForm.confirmPassword
              ? ""
              : "Passwords do not match";
          break;
      }
    },
    handleSchoolSearch(event) {
      const query = event.target.value;
      this.schoolSearchQuery = query;

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      if (!query) {
        this.schoolResults = [];
        this.searchPerformed = false;
        this.schoolSearchLoading = false;
        return;
      }

      this.schoolSearchLoading = true;

      this.searchTimeout = setTimeout(() => {
        this.performSchoolSearch(query.trim());
      }, 1000);
    },

    async performSchoolSearch(query) {
      try {
        const isCeebCode = /^\d{6}$/.test(query);

        let response;
        if (isCeebCode) {
          response = await apiClient.post("/api/viva/validate_ceeb/", {
            ceeb: query,
          });

          if (response.data.status === 200 && response.data.hs) {
            this.schoolResults = [response.data.hs];
          } else {
            this.schoolResults = [];
          }
        } else {
          response = await apiClient.get("/api/viva/search_schools/", {
            params: { query },
          });

          if (response.data.status === 200) {
            this.schoolResults = response.data.hs_list || [];
          } else {
            this.schoolResults = [];
          }
        }

        this.searchPerformed = true;
      } catch (error) {
        console.error("School search error:", error);
        this.schoolResults = [];
        this.searchPerformed = true;
      } finally {
        this.schoolSearchLoading = false;
      }
    },

    selectSchool(school) {
      this.selectedSchool = school;
      this.schoolResults = [];
      this.schoolSearchQuery = "";
      this.searchPerformed = false;
      this.errors.general = "";
    },

    clearSchoolSelection() {
      this.selectedSchool = null;
      this.schoolSearchQuery = "";
      this.schoolResults = [];
      this.searchPerformed = false;
      this.schoolNameFallback = "";
    },

    async handleRegister() {
      if (!this.isFormValid) return;

      this.loading = true;
      this.errors = {};

      try {
        const registrationData = {
          email: this.registerForm.email.trim(),
          password: this.registerForm.password,
          first_name: this.registerForm.firstName.trim(),
          last_name: this.registerForm.lastName.trim(),
          email_personal: this.registerForm.emailPersonal.trim() || null,
          school_ceeb: this.selectedSchool?.ceeb_code || null,
          school_name: !this.selectedSchool ? this.schoolNameFallback.trim() || null : null,
          role_type: this.registerForm.roleType,
        };

        const response = await apiClient.post(
          "/api/viva/instructor_register/",
          registrationData,
        );

        if (response.data.status === 200) {
          this.showSuccessModal = true;
        }
      } catch (error) {
        console.error("Registration error:", error);

        if (error.response?.status === 403) {
          this.showAccountExistsModal = true;
        } else if (error.response?.status === 400) {
          this.errors.general =
            error.response.data.message ||
            "Please check your information and try again.";
        } else {
          this.errors.general = "Registration failed. Please try again.";
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
@import "../styles/viva-classes.css";

.register-page {
  background-color: var(--viva-cream);
  min-height: 100vh;
  padding: 0;
  position: relative;
}

.viva-page-header {
  height: auto;
}

.register-title {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.register-title > * {
  margin: 0px;
}

.register-subtitle {
  color: var(--viva-dark);
  font-size: 20px;
  font-weight: 500;
  line-height: 1.4;
}

.page-content {
  display: flex;
  align-items: center;
}

.container-fluid {
  width: 100%;
}

.form-left {
  padding: 40px;
  border-right: 2px solid var(--viva-dark);
}

.form-right {
  padding: 40px;
}

/* Role Selection */
.role-section {
  margin-bottom: 30px;
}

.role-options {
  display: flex;
  gap: 20px;
  margin-top: 10px;
}

.role-option {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  border: 2px solid var(--viva-green);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--viva-white);
}

.role-option:hover {
  border-color: var(--viva-orange);
}

.role-selected {
  background: var(--viva-green);
  color: var(--viva-white);
}

.role-radio {
  margin-right: 10px;
}

.role-label {
  font-size: 16px;
  font-weight: 600;
}

/* School Search */
.school-section {
  margin-bottom: 30px;
}

.school-instruction {
  color: var(--viva-orange);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 15px 0;
  line-height: 1.4;
}

.search-container {
  position: relative;
  margin-bottom: 15px;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--viva-orange);
  font-size: 18px;
}

.school-search-input {
  width: 100%;
  padding: 15px 18px 15px 50px;
  border: 2px solid var(--viva-green);
  border-radius: 8px;
  font-size: 16px;
  background: var(--viva-white);
  transition: all 0.2s ease;
}

.school-search-input:focus {
  outline: none;
  border-color: var(--viva-orange);
  box-shadow: 0 0 8px rgba(245, 176, 65, 0.2);
}

.school-results-dropdown {
  border: 1px solid var(--viva-green);
  border-top: none;
  border-radius: 0 0 8px 8px;
  background: var(--viva-white);
  max-height: 300px;
  overflow-y: auto;
  position: relative;
  width: 100%;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.school-result-item {
  padding: 12px 18px;
  cursor: pointer;
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s ease;
}

.school-result-item:hover {
  background-color: var(--viva-cream);
}

.school-result-item:last-child {
  border-bottom: none;
}

.school-result-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.school-name {
  font-weight: 600;
  color: var(--viva-dark);
  font-size: 16px;
}

.school-location {
  color: var(--viva-orange);
  font-size: 14px;
}

.school-ceeb {
  color: var(--viva-green);
  font-size: 12px;
  font-weight: 500;
}

.selected-school-display {
  margin-top: 15px;
  padding: 15px;
  background: var(--viva-white);
  border-radius: 8px;
  border: 2px solid var(--viva-green);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.clear-school-btn {
  font-size: 14px;
  width: 25%;
}

.search-loading-icon {
  position: absolute;
  right: 18px;
  margin-top: 17px;
  transform: translateY(-50%);
  color: var(--viva-orange);
}

.no-results-message {
  padding: 15px 18px;
  color: var(--viva-orange);
  font-style: italic;
  text-align: center;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f9f9f9;
  margin-top: 10px;
}

.form-actions {
  text-align: right;
}

.create-account-btn {
  background: var(--viva-green);
  color: white;
  border: none;
  border-radius: 25px;
  padding: 15px 30px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  transition: all 0.2s ease;
}

.create-account-btn:hover:not(:disabled) {
  background: var(--viva-green);
  transform: translateY(-1px);
}

.create-account-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.bottom-links {
  margin-top: 30px;
  text-align: center;
}

@media (min-width: 768px) {
  .form-sections {
    display: flex;
    align-items: center;
  }
}

@media (max-width: 767px) {
  .form-left {
    border-right: none;
  }

  .form-left,
  .form-right {
    padding: 20px;
  }

  .role-options {
    flex-direction: column;
  }
}

.optional-label {
  font-size: 13px;
  font-weight: 400;
  color: var(--viva-grey);
  text-transform: none;
  margin-left: 4px;
}

.terms-section {
  margin-bottom: 30px;
}

.terms-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 16px;
  color: var(--viva-dark);
}

.checkbox-input {
  display: none;
}

.checkmark {
  width: 24px;
  height: 24px;
  border: 2px solid var(--viva-green);
  border-radius: 4px;
  margin-right: 15px;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.checkbox-input:checked + .checkmark {
  background-color: var(--viva-green);
  border-color: var(--viva-green);
}

.checkbox-input:checked + .checkmark::after {
  content: "✓";
  color: white;
  font-weight: bold;
}
</style>
